import React, { useState, useEffect } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import CalendlyModal from "@/components/tchatix/CalendlyModal";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { Image } from "@/components/ui/image";

const CALENDLY_URL = "https://calendly.com/tchatixdemo/audit?background_color=001d31&text_color=ffffff&primary_color=0985d8";

const navLinks = [
  { key: "home", href: "#top" },
  { key: "demo", demo: true },
  { key: "tarifs", href: "/tarifs" },
  { key: "simulateur", href: "/simulateur" },
  { key: "agentBuilder", href: "/agent-builder" },
  { key: "blog", href: "/blog" },
  { key: "contact", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const enPrefix = location.pathname.startsWith("/en") ? "/en" : "";
  const handleLink = (link) => {
    setMobileOpen(false);
    if (link.demo) {
      setDemoOpen(true);
      return;
    }
    if (link.external) {
      window.open(link.href, "_blank", "noopener,noreferrer");
      return;
    }
    if (link.href.startsWith("#")) {
      if (location.pathname !== "/" && location.pathname !== "/en") {
        navigate(enPrefix || "/");
        setTimeout(() => document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(enPrefix + link.href);
    }
  };

  return (
    /*
      FIX page blanche mobile : suppression de l'animation d'entrée sur le <nav>.
      Un élément position:fixed qui anime transform/opacity crée une couche de
      composition cassée sur iOS Safari → page blanche au chargement, contenu
      qui n'apparaît qu'au scroll. Ici : affichage instantané, aucun layer.
    */
    <nav className="fixed top-0 inset-x-0 z-50 flex justify-center px-4 pt-4">
      <style>{`
        @keyframes dropIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .nav-drop { animation: dropIn .25s ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .nav-drop { animation: none; opacity: 1; transform: none; }
        }
      `}</style>

      <div
        className={`flex items-center justify-between gap-6 px-5 md:px-7 rounded-full transition-all duration-300 ease-in-out ${
          scrolled ? "max-w-[900px] py-2 bg-white border border-[#001D31]/10" : "max-w-[1200px] w-full py-3 bg-transparent"
        }`}>

        {/* Logo */}
        <a href="#top" className="flex items-center gap-2 shrink-0" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
          <Image
            src="https://media.base44.com/images/public/6a69bb99139c43be5222264c/db430a032_Tchatix.png"
            alt="Tchatix"
            fittingType="fit"
            className="h-8 w-8"
            draggable={false} />
        </a>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) =>
            <button
              key={link.key}
              id={link.demo ? "cta-calendly-nav" : undefined}
              onClick={() => handleLink(link)}
              className="text-sm text-[#0985D8] hover:text-[#8896B0] transition-colors duration-200">
              {t(`nav.${link.key}`)}
            </button>
          )}
        </div>

        {/* CTA + language */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <LanguageSwitcher />
          <a
            href="https://app.tchatix.ai/auth/sign-in"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-sm font-medium hover:scale-[1.03] transition-all duration-300 [font-family:'Poppins',_sans-serif] ring-1 ring-[#FCE300]/30"
            style={{ background: "linear-gradient(120deg, #001D31 0%, #0A3A5C 60%, #001D31 100%)" }}>{t("nav.login")}</a>
        </div>

        {/* Mobile toggle */}
        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <button
            className="text-[#001D31]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden absolute top-20 inset-x-4 bg-white border border-[#001D31]/10 rounded-3xl p-6 flex flex-col gap-4 nav-drop">
          {navLinks.map((link) =>
            <button
              key={link.key}
              id={link.demo ? "cta-calendly-nav-mobile" : undefined}
              onClick={() => handleLink(link)}
              className="text-left text-[#0985D8] hover:text-[#001D31] transition-colors py-2">
              {t(`nav.${link.key}`)}
            </button>
          )}
          <a
            href="https://app.tchatix.ai/auth/sign-in"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full text-[#001D31] text-sm font-medium ring-1 ring-[#001D31]/15 hover:bg-[#001D31]/5 transition-colors">
            {t("nav.login")}
          </a>
          <a
            href="https://calendly.com/tchatixdemo/audit"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full text-white text-sm font-medium ring-1 ring-[#FCE300]/30"
            style={{ background: "linear-gradient(120deg, #001D31 0%, #0A3A5C 60%, #001D31 100%)" }}>
            {t("nav.tryFree")}
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      )}

      {/* ── Démo pop-up (Calendly) ── */}
      <CalendlyModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </nav>
  );
}